import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Shield, 
  AlertTriangle, 
  CheckCircle2, 
  XCircle,
  Clock,
  Users,
  TrendingUp,
  TrendingDown,
  Activity,
  FileCheck,
  Lock,
  Unlock,
  RefreshCw,
  ChevronRight,
  AlertCircle,
  Zap,
  Eye,
  Bell
} from 'lucide-react';
import { useExchangeAuth } from '../../contexts/ExchangeAuthContext';

// Risk level badge component
const RiskBadge = ({ level }) => {
  const config = {
    low: { bg: 'bg-neon-green/20', text: 'text-neon-green', label: 'LOW RISK' },
    medium: { bg: 'bg-yellow-500/20', text: 'text-yellow-500', label: 'MEDIUM' },
    high: { bg: 'bg-orange-500/20', text: 'text-orange-500', label: 'HIGH RISK' },
    critical: { bg: 'bg-red-500/20', text: 'text-red-500', label: 'CRITICAL' }
  };
  
  const { bg, text, label } = config[level] || config.low;
  
  return (
    <span className={`px-2 py-1 rounded-full text-xs font-bold ${bg} ${text}`}>
      {label}
    </span>
  );
};

// Score circle component
const ScoreCircle = ({ score, size = 'md' }) => {
  const sizeClasses = {
    sm: 'w-12 h-12 text-lg',
    md: 'w-20 h-20 text-2xl',
    lg: 'w-28 h-28 text-4xl'
  };
  
  const getColor = (s) => {
    if (s >= 80) return 'text-neon-green border-neon-green';
    if (s >= 60) return 'text-yellow-500 border-yellow-500';
    if (s >= 40) return 'text-orange-500 border-orange-500';
    return 'text-red-500 border-red-500';
  };
  
  return (
    <div className={`${sizeClasses[size]} rounded-full border-4 ${getColor(score)} flex items-center justify-center font-bold`}>
      {score}
    </div>
  );
};

// Stat card component
const StatCard = ({ icon: Icon, label, value, change, trend, color = 'neon-cyan' }) => (
  <div className="glass-card p-4">
    <div className="flex items-start justify-between">
      <div className={`w-10 h-10 rounded-lg bg-${color}/20 flex items-center justify-center`}>
        <Icon className={`w-5 h-5 text-${color}`} />
      </div>
      {trend && (
        <span className={`text-xs flex items-center ${trend === 'up' ? 'text-neon-green' : 'text-red-500'}`}>
          {trend === 'up' ? <TrendingUp className="w-3 h-3 mr-1" /> : <TrendingDown className="w-3 h-3 mr-1" />}
          {change}
        </span>
      )}
    </div>
    <div className="mt-3">
      <div className="text-2xl font-bold text-white">{value}</div>
      <div className="text-sm text-slate-400">{label}</div>
    </div>
  </div>
);

// Driver compliance row
const DriverComplianceRow = ({ driver, onViewDetails }) => {
  const getStatusIcon = (allowed) => {
    if (allowed) return <CheckCircle2 className="w-5 h-5 text-neon-green" />;
    return <XCircle className="w-5 h-5 text-red-500" />;
  };
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="flex items-center justify-between p-4 bg-white/5 rounded-lg hover:bg-white/10 transition-colors cursor-pointer"
      onClick={() => onViewDetails(driver)}
    >
      <div className="flex items-center space-x-4">
        <ScoreCircle score={driver.score} size="sm" />
        <div>
          <div className="text-white font-medium">{driver.name}</div>
          <div className="text-sm text-slate-400">
            {driver.issues_count} issue{driver.issues_count !== 1 ? 's' : ''}
          </div>
        </div>
      </div>
      <div className="flex items-center space-x-4">
        <RiskBadge level={driver.risk_level} />
        {getStatusIcon(driver.dispatch_allowed)}
        <ChevronRight className="w-5 h-5 text-slate-400" />
      </div>
    </motion.div>
  );
};

// Alert item component
const AlertItem = ({ alert }) => {
  const getSeverityColor = (type) => {
    switch (type) {
      case 'emergency': return 'border-red-500 bg-red-500/10';
      case 'critical': return 'border-orange-500 bg-orange-500/10';
      case 'warning': return 'border-yellow-500 bg-yellow-500/10';
      default: return 'border-slate-500 bg-slate-500/10';
    }
  };
  
  return (
    <div className={`p-3 rounded-lg border-l-4 ${getSeverityColor(alert.type)}`}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-white text-sm font-medium">{alert.message}</div>
          <div className="text-xs text-slate-400 mt-1">
            {new Date(alert.created_at).toLocaleString()}
          </div>
        </div>
        <AlertCircle className={`w-4 h-4 ${
          alert.type === 'emergency' ? 'text-red-500' :
          alert.type === 'critical' ? 'text-orange-500' :
          'text-yellow-500'
        }`} />
      </div>
    </div>
  );
};

// Expiring credential item
const ExpiringItem = ({ credential }) => {
  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return 'text-red-500';
      case 'warning': return 'text-yellow-500';
      default: return 'text-slate-400';
    }
  };
  
  return (
    <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
      <div className="flex items-center space-x-3">
        <Clock className={`w-5 h-5 ${getSeverityColor(credential.severity)}`} />
        <div>
          <div className="text-white text-sm">{credential.credential_type}</div>
          <div className="text-xs text-slate-400">Driver: {credential.owner_id?.slice(-8)}</div>
        </div>
      </div>
      <div className={`text-sm font-medium ${getSeverityColor(credential.severity)}`}>
        {credential.days_until_expiry} days
      </div>
    </div>
  );
};

export const ComplianceDashboard = () => {
  const { organization } = useExchangeAuth();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [summary, setSummary] = useState(null);
  const [driverScores, setDriverScores] = useState([]);
  const [expiring, setExpiring] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [lockouts, setLockouts] = useState([]);
  const [selectedDriver, setSelectedDriver] = useState(null);
  
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const providerId = organization?._id || organization?.id;
  
  const fetchData = async () => {
    if (!providerId) return;
    
    try {
      const [summaryRes, scoresRes, expiringRes, alertsRes, lockoutsRes] = await Promise.all([
        fetch(`${API_URL}/api/xchange/compliance/dashboard/summary?provider_id=${providerId}`),
        fetch(`${API_URL}/api/xchange/compliance/scores/provider/${providerId}`),
        fetch(`${API_URL}/api/xchange/compliance/dashboard/expiring?days=30&provider_id=${providerId}`),
        fetch(`${API_URL}/api/xchange/compliance/dashboard/alerts?provider_id=${providerId}&limit=10`),
        fetch(`${API_URL}/api/xchange/compliance/lockouts/active?provider_id=${providerId}`)
      ]);
      
      const summaryData = await summaryRes.json();
      const scoresData = await scoresRes.json();
      const expiringData = await expiringRes.json();
      const alertsData = await alertsRes.json();
      const lockoutsData = await lockoutsRes.json();
      
      setSummary(summaryData);
      setDriverScores(scoresData.scores || []);
      setExpiring(expiringData.credentials || []);
      setAlerts(alertsData.alerts || []);
      setLockouts(lockoutsData.lockouts || []);
    } catch (error) {
      console.error('Failed to fetch compliance data:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };
  
  useEffect(() => {
    fetchData();
    
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [providerId]);
  
  const handleRefresh = () => {
    setRefreshing(true);
    fetchData();
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 text-neon-cyan animate-spin" />
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold text-white">Compliance Dashboard</h1>
          <p className="text-slate-400 text-sm mt-1">Real-time fleet compliance monitoring</p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="btn-secondary flex items-center space-x-2"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          <span>Refresh</span>
        </button>
      </div>
      
      {/* Live Status Banner */}
      <div className="flex items-center space-x-2 px-4 py-2 rounded-lg bg-neon-green/10 border border-neon-green/30">
        <span className="relative flex h-3 w-3">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-neon-green opacity-75"></span>
          <span className="relative inline-flex rounded-full h-3 w-3 bg-neon-green"></span>
        </span>
        <span className="text-neon-green text-sm font-mono">
          MONITORING ACTIVE • {summary?.total_drivers || 0} DRIVERS • {summary?.active_lockouts || 0} LOCKOUTS
        </span>
      </div>
      
      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={Users}
          label="Total Drivers"
          value={summary?.total_drivers || 0}
          color="neon-cyan"
        />
        <StatCard
          icon={CheckCircle2}
          label="Dispatch Ready"
          value={summary?.dispatch_ready || 0}
          change={`${summary?.compliance_rate || 0}%`}
          trend="up"
          color="neon-green"
        />
        <StatCard
          icon={AlertTriangle}
          label="At Risk"
          value={(summary?.warning || 0) + (summary?.non_compliant || 0)}
          color="yellow-500"
        />
        <StatCard
          icon={Lock}
          label="Blocked"
          value={summary?.dispatch_blocked || 0}
          color="red-500"
        />
      </div>
      
      {/* Main Content Grid */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Driver Scores List */}
        <div className="lg:col-span-2 glass-card p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-white flex items-center">
              <Activity className="w-5 h-5 mr-2 text-neon-cyan" />
              Driver Compliance Scores
            </h2>
            <span className="text-sm text-slate-400">
              Sorted by risk level
            </span>
          </div>
          
          <div className="space-y-3 max-h-96 overflow-y-auto pr-2">
            {driverScores.length > 0 ? (
              driverScores.map((driver, idx) => (
                <DriverComplianceRow
                  key={driver.driver_id}
                  driver={driver}
                  onViewDetails={setSelectedDriver}
                />
              ))
            ) : (
              <div className="text-center py-8 text-slate-400">
                <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>No drivers found</p>
              </div>
            )}
          </div>
        </div>
        
        {/* Right Sidebar */}
        <div className="space-y-6">
          {/* Active Lockouts */}
          {lockouts.length > 0 && (
            <div className="glass-card p-4 border-red-500/30">
              <h3 className="text-white font-semibold flex items-center mb-3">
                <Lock className="w-4 h-4 mr-2 text-red-500" />
                Active Lockouts ({lockouts.length})
              </h3>
              <div className="space-y-2">
                {lockouts.slice(0, 3).map((lockout, idx) => (
                  <div key={idx} className="p-3 bg-red-500/10 rounded-lg border border-red-500/20">
                    <div className="text-white text-sm font-medium">{lockout.driver_name}</div>
                    <div className="text-xs text-red-400 mt-1">{lockout.reason}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Expiring Credentials */}
          <div className="glass-card p-4">
            <h3 className="text-white font-semibold flex items-center mb-3">
              <Clock className="w-4 h-4 mr-2 text-yellow-500" />
              Expiring Soon ({expiring.length})
            </h3>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {expiring.length > 0 ? (
                expiring.slice(0, 5).map((cred, idx) => (
                  <ExpiringItem key={idx} credential={cred} />
                ))
              ) : (
                <p className="text-sm text-slate-400 text-center py-4">
                  No credentials expiring soon
                </p>
              )}
            </div>
          </div>
          
          {/* Recent Alerts */}
          <div className="glass-card p-4">
            <h3 className="text-white font-semibold flex items-center mb-3">
              <Bell className="w-4 h-4 mr-2 text-neon-cyan" />
              Recent Alerts
            </h3>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {alerts.length > 0 ? (
                alerts.slice(0, 5).map((alert, idx) => (
                  <AlertItem key={idx} alert={alert} />
                ))
              ) : (
                <p className="text-sm text-slate-400 text-center py-4">
                  No recent alerts
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Compliance Score Breakdown Legend */}
      <div className="glass-card p-6">
        <h3 className="text-white font-semibold mb-4 flex items-center">
          <Zap className="w-5 h-5 mr-2 text-neon-cyan" />
          Compliance Score Breakdown
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
          {[
            { label: 'Credentials', weight: '25%', icon: FileCheck },
            { label: 'Background', weight: '20%', icon: Shield },
            { label: 'DMV/MVR', weight: '20%', icon: Activity },
            { label: 'Drug Screen', weight: '15%', icon: AlertCircle },
            { label: 'Insurance', weight: '10%', icon: CheckCircle2 },
            { label: 'History', weight: '10%', icon: Clock }
          ].map((item, idx) => (
            <div key={idx} className="text-center p-3 bg-white/5 rounded-lg">
              <item.icon className="w-6 h-6 mx-auto mb-2 text-neon-cyan" />
              <div className="text-white text-sm font-medium">{item.label}</div>
              <div className="text-neon-green text-xs">{item.weight}</div>
            </div>
          ))}
        </div>
      </div>
      
      {/* Driver Detail Modal */}
      <AnimatePresence>
        {selectedDriver && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
            onClick={() => setSelectedDriver(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="glass-card p-6 max-w-lg w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-4">
                  <ScoreCircle score={selectedDriver.score} />
                  <div>
                    <h3 className="text-xl font-bold text-white">{selectedDriver.name}</h3>
                    <RiskBadge level={selectedDriver.risk_level} />
                  </div>
                </div>
                <button
                  onClick={() => setSelectedDriver(null)}
                  className="text-slate-400 hover:text-white"
                >
                  ✕
                </button>
              </div>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                  <span className="text-slate-400">Dispatch Status</span>
                  <span className={selectedDriver.dispatch_allowed ? 'text-neon-green' : 'text-red-500'}>
                    {selectedDriver.dispatch_allowed ? 'ALLOWED' : 'BLOCKED'}
                  </span>
                </div>
                
                <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                  <span className="text-slate-400">Open Issues</span>
                  <span className="text-white">{selectedDriver.issues_count}</span>
                </div>
                
                <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                  <span className="text-slate-400">Driver ID</span>
                  <span className="text-white font-mono text-sm">{selectedDriver.driver_id}</span>
                </div>
              </div>
              
              <div className="mt-6 flex space-x-3">
                <button className="btn-primary flex-1">
                  View Full Profile
                </button>
                {!selectedDriver.dispatch_allowed && (
                  <button className="btn-secondary flex items-center justify-center space-x-2">
                    <Unlock className="w-4 h-4" />
                    <span>Request Unlock</span>
                  </button>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ComplianceDashboard;
